/**
 * availableSlotsLogic - Pure logic extracted from useAvailableSlots
 *
 * Functions for slot data transformation, availability checking,
 * cutoff enforcement, and capacity validation.
 * No React, no Supabase - just data in, data out.
 */

import type { AvailableSlot, SlotType } from '@/lib/scheduling';

// ---------------------------------------------------------------------------
// Slot transformation (API response normalization)
// ---------------------------------------------------------------------------

/**
 * Raw slot shape from the scheduling-availability edge function.
 * Supports both camelCase and snake_case field names.
 */
export interface RawSlot {
  slotId?: string;
  id?: string;
  startTime?: string;
  start_time?: string;
  endTime?: string;
  end_time?: string;
  slotType?: string;
  slot_type?: string;
  instructorId?: string;
  instructor_id?: string;
  instructorName?: string;
  instructor_name?: string;
  currentBookings?: number;
  spotsAvailable?: number;
  maxCapacity?: number;
  max_capacity?: number;
  minCancellationNoticeHours?: number;
  min_cancellation_notice_hours?: number;
}

/**
 * Normalize a raw API slot into the canonical AvailableSlot shape.
 * Handles both camelCase and snake_case field names from the edge function.
 */
export function transformSlot(raw: RawSlot): AvailableSlot {
  return {
    id: raw.slotId || raw.id || '',
    startTime: raw.startTime || raw.start_time || '',
    endTime: raw.endTime || raw.end_time || '',
    slotType: (raw.slotType || raw.slot_type || 'lesson') as SlotType,
    instructorId: raw.instructorId || raw.instructor_id || '',
    instructorName: raw.instructorName || raw.instructor_name || 'TBD',
    currentBookings: raw.currentBookings ?? (raw.spotsAvailable === 0 ? 1 : 0),
    maxCapacity: raw.maxCapacity || raw.max_capacity || 1,
    minCancellationNoticeHours: raw.minCancellationNoticeHours ?? raw.min_cancellation_notice_hours ?? 24,
  };
}

/**
 * Transform an array of raw API slots into canonical AvailableSlot[].
 */
export function transformSlots(rawSlots: RawSlot[]): AvailableSlot[] {
  return rawSlots.map(transformSlot);
}

// ---------------------------------------------------------------------------
// Availability & capacity
// ---------------------------------------------------------------------------

/**
 * Whether a slot has remaining capacity for a new booking.
 */
export function hasCapacity(slot: AvailableSlot): boolean {
  return slot.currentBookings < slot.maxCapacity;
}

/**
 * Filter an array of slots to only those with remaining capacity.
 */
export function filterAvailableSlots(slots: AvailableSlot[]): AvailableSlot[] {
  return slots.filter(hasCapacity);
}

/**
 * Remaining spots in a slot.
 */
export function spotsRemaining(slot: AvailableSlot): number {
  return Math.max(0, slot.maxCapacity - slot.currentBookings);
}

// ---------------------------------------------------------------------------
// Cutoff / cancellation notice enforcement
// ---------------------------------------------------------------------------

/**
 * Whether a slot's start time is far enough in the future to satisfy
 * its cancellation notice requirement. Slots too close to now should
 * not be bookable (the parent wouldn't be able to cancel in time).
 *
 * @param slot - The slot to check
 * @param now  - Current time (injectable for testing)
 */
export function isSlotBookable(slot: AvailableSlot, now: Date = new Date()): boolean {
  if (!slot.startTime) return false;

  const startMs = new Date(slot.startTime).getTime();
  const nowMs = now.getTime();

  // Slot must be in the future
  if (startMs <= nowMs) return false;

  // Must satisfy cancellation notice window
  const noticeHours = slot.minCancellationNoticeHours ?? 24;
  const noticeMs = noticeHours * 60 * 60 * 1000;
  return (startMs - nowMs) >= noticeMs;
}

/**
 * Filter slots to only those that are bookable (future + within notice window + capacity).
 */
export function filterBookableSlots(slots: AvailableSlot[], now: Date = new Date()): AvailableSlot[] {
  return slots.filter(slot => hasCapacity(slot) && isSlotBookable(slot, now));
}

// ---------------------------------------------------------------------------
// Slot pagination (used by support chat booking flow)
// ---------------------------------------------------------------------------

export const DEFAULT_SLOTS_PER_PAGE = 5;

export interface SlotPage {
  /** Slots for the current page */
  pageSlots: AvailableSlot[];
  /** Whether there are more slots after this page */
  hasMore: boolean;
  /** Number of remaining slots after this page */
  remaining: number;
  /** Total number of slots */
  total: number;
}

/**
 * Get a page of slots from a full list, given an offset and page size.
 */
export function getSlotPage(
  slots: AvailableSlot[],
  offset: number,
  perPage: number = DEFAULT_SLOTS_PER_PAGE,
): SlotPage {
  const safeOffset = Math.max(0, Math.min(offset, slots.length));
  const pageSlots = slots.slice(safeOffset, safeOffset + perPage);
  const remaining = Math.max(0, slots.length - (safeOffset + perPage));

  return {
    pageSlots,
    hasMore: remaining > 0,
    remaining,
    total: slots.length,
  };
}

/**
 * Compute the next offset when the user asks for "more" slots.
 * Wraps around to 0 when past the end.
 */
export function nextPageOffset(
  currentOffset: number,
  totalSlots: number,
  perPage: number = DEFAULT_SLOTS_PER_PAGE,
): number {
  const next = currentOffset + perPage;
  return next >= totalSlots ? 0 : next;
}

// ---------------------------------------------------------------------------
// Slot selection parsing (support chat numeric input)
// ---------------------------------------------------------------------------

export interface SlotSelection {
  /** Whether the input was a valid slot selection */
  valid: boolean;
  /** 0-based index into the full slots array */
  slotIndex: number;
  /** The selected slot, or undefined if invalid */
  slot?: AvailableSlot;
}

/**
 * Parse a user's numeric slot selection (1-based) and resolve it
 * to an actual slot from the paginated list.
 *
 * @param input       - Raw user input (e.g. "2")
 * @param slots       - Full array of available slots
 * @param offset      - Current pagination offset
 * @param perPage     - Slots shown per page
 */
export function parseSlotSelection(
  input: string,
  slots: AvailableSlot[],
  offset: number,
  perPage: number = DEFAULT_SLOTS_PER_PAGE,
): SlotSelection {
  const trimmed = input.trim();
  const num = parseInt(trimmed, 10);

  if (isNaN(num) || num < 1 || num > perPage) {
    return { valid: false, slotIndex: -1 };
  }

  const actualIndex = offset + (num - 1);
  const slot = slots[actualIndex];

  if (!slot) {
    return { valid: false, slotIndex: actualIndex };
  }

  return { valid: true, slotIndex: actualIndex, slot };
}

// ---------------------------------------------------------------------------
// Bulk cancel summary
// ---------------------------------------------------------------------------

/**
 * Build a human-readable summary after a bulk cancel operation.
 */
export function buildBulkCancelSummary(
  successCount: number,
  totalCount: number,
): string {
  if (successCount === totalCount) {
    return `All ${successCount} classes have been cancelled. Is there anything else I can help with?`;
  }
  return `Cancelled ${successCount} of ${totalCount} classes. Some may have failed - please check your schedule.`;
}

// ---------------------------------------------------------------------------
// Child ID validation
// ---------------------------------------------------------------------------

/**
 * Whether a childId value is valid for API calls.
 * Rejects undefined, null, empty, and stringified null/undefined.
 */
export function isValidChildId(childId: string | null | undefined): childId is string {
  if (!childId) return false;
  if (childId === 'undefined' || childId === 'null') return false;
  return childId.trim().length > 0;
}
